import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Wallet, Search as SearchIcon, MessageSquare, Send, FileDown, Calendar } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { t, formatMoney } from "@/i18n";
import { propertyTypeLabel } from "@/lib/propertyTypes";
import Header from "@/components/Header";
import LegalFooter from "@/components/LegalFooter";
import ChatModal from "@/components/ChatModal";
import Money from "@/components/Money";
import TranslateButton from "@/components/TranslateButton";
import { logWarn } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

function InfoTile({ icon, label, value, testid }) {
  return (
    <div className="rounded-lg border border-[#D8CFBB] bg-[#FBF8F1] p-4" data-testid={testid}>
      <div className="w-8 h-8 mb-2 text-[#C5A880]">
        {React.cloneElement(icon, { className: "w-5 h-5" })}
      </div>
      <p className="text-[10px] tracking-[0.2em] uppercase text-[#B8A98F]">{label}</p>
      <p className="text-lg font-[Cormorant_Garamond,serif] text-[#1A1610] mt-1">{value}</p>
    </div>
  );
}

function ContactBox({ mandateId, myProperties, lang, onCreated }) {
  const [open, setOpen] = useState(false);
  const [propertyId, setPropertyId] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!propertyId) {
      toast.error(lang === "it" ? "Seleziona un immobile" : "Select a property");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/matches/contact", { mandate_id: mandateId, property_id: propertyId, message });
      toast.success(lang === "it" ? "Contatto avviato tramite Aaron & Partners" : "Contact started via Aaron & Partners");
      setOpen(false);
      setMessage("");
      setPropertyId("");
      onCreated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  if (!myProperties?.length) {
    return (
      <Card className="bg-[#FBF8F1] border-[#D8CFBB] p-6" data-testid="search-detail-no-properties">
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880] mb-2">
          {lang === "it" ? "Proponi un immobile" : "Offer a property"}
        </p>
        <p className="text-sm text-[#5A5040]">
          {lang === "it"
            ? "Per contattare questo buyer devi prima pubblicare almeno un immobile."
            : "To contact this buyer you must first list at least one property."}
        </p>
      </Card>
    );
  }

  return (
    <Card className="bg-[#FBF8F1] border-[#D8CFBB] p-6" data-testid="search-detail-contact-box">
      <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880] mb-3">
        {lang === "it" ? "Contatta il buyer" : "Contact the buyer"}
      </p>
      <p className="text-sm text-[#5A5040] mb-4">
        {lang === "it"
          ? "AARON & PARTNERS S.R.L. genererà un contratto di intermediazione. Il buyer riceverà una notifica e potrà firmarlo con OTP."
          : "AARON & PARTNERS S.R.L. will generate an intermediation contract. The buyer receives a notification and can sign with OTP."}
      </p>
      <Button
        onClick={() => setOpen(true)}
        data-testid="search-detail-contact-btn"
        className="bg-[#1A1610] text-[#F5F0E6] hover:bg-[#2E2A22] rounded-full"
      >
        <MessageSquare className="h-4 w-4 me-2" />
        {lang === "it" ? "Contatta tramite Aaron & Partners" : "Contact via Aaron & Partners"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FFFAF0] border-[#D8CFBB] text-[#1A1610] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-light" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              {lang === "it" ? "Contatta tramite Aaron & Partners" : "Contact via Aaron & Partners"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs text-[#7A6E5A] mb-1 block">
                {lang === "it" ? "Immobile da proporre" : "Property to offer"}
              </label>
              <Select value={propertyId} onValueChange={setPropertyId}>
                <SelectTrigger
                  data-testid="search-detail-property-select"
                  className="bg-[#F5F0E6] border-[#D8CFBB] text-[#1A1610]"
                >
                  <SelectValue placeholder={lang === "it" ? "Scegli…" : "Choose…"} />
                </SelectTrigger>
                <SelectContent>
                  {myProperties.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.title} — {p.city} · {formatMoney(p.price, lang)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Textarea
              data-testid="search-detail-contact-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder={lang === "it" ? "Messaggio (opzionale) per il buyer…" : "Optional message for the buyer…"}
              className="bg-[#F5F0E6] border-[#D8CFBB] text-[#1A1610] focus-visible:ring-[#C5A880]"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-[#4A3F30]">
              {lang === "it" ? "Annulla" : "Cancel"}
            </Button>
            <Button
              onClick={submit}
              disabled={busy || !propertyId}
              data-testid="search-detail-contact-submit"
              className="bg-[#C5A880] text-black hover:bg-[#d9bf94] rounded-full disabled:opacity-40"
            >
              <Send className="h-4 w-4 me-2" />
              {(() => {
                if (busy) return "…";
                return lang === "it" ? "Invia contatto" : "Send contact";
              })()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function ContractRow({ contract, lang, onOpenChat, onDownloadPdf }) {
  const signed = !!contract.buyer_signed;
  return (
    <div
      className="rounded-lg border border-[#D8CFBB] bg-[#FBF8F1] p-4 flex flex-col sm:flex-row sm:items-center gap-3"
      data-testid={`search-detail-contract-${contract.id}`}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm text-[#1A1610] font-medium truncate">
          {contract.property_title} — {contract.property_city}
        </p>
        <p className="text-xs text-[#7A6E5A] mt-1">
          {lang === "it" ? "Codice" : "Code"} {contract.id.slice(0, 8).toUpperCase()} ·{" "}
          {new Date(contract.created_at).toLocaleDateString()}
        </p>
        {!signed && (
          <p className="text-[11px] text-amber-800 mt-1" data-testid={`search-detail-chat-locked-${contract.id}`}>
            {lang === "it"
              ? "Chat sbloccata dopo firma bilaterale (OTP del buyer)."
              : "Chat unlocks after bilateral OTP signature."}
          </p>
        )}
      </div>
      <Badge className={signed ? "bg-emerald-700 text-white" : "bg-amber-500 text-black"}>
        {(() => {
          if (signed) return lang === "it" ? "Firmato" : "Signed";
          return lang === "it" ? "In attesa" : "Pending";
        })()}
      </Badge>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => onDownloadPdf(contract)}
          data-testid={`search-detail-pdf-${contract.id}`}
          className="border-[#D8CFBB] text-[#4A3F30] hover:bg-[#F5F0E6] rounded-full"
        >
          <FileDown className="h-4 w-4 me-1.5" /> PDF
        </Button>
        <TranslateButton kind="intermediation" id={contract.id} />
        <Button
          size="sm"
          onClick={() => onOpenChat(contract)}
          disabled={!signed}
          data-testid={`search-detail-chat-${contract.id}`}
          className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <MessageSquare className="h-4 w-4 me-1.5" />
          {lang === "it" ? "Chat" : "Chat"}
        </Button>
      </div>
    </div>
  );
}

export default function SearchDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { lang, user, loading } = useApp();
  const [mandate, setMandate] = useState(null);
  const [err, setErr] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [myProperties, setMyProperties] = useState([]);
  const [chatContract, setChatContract] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/mandates/${id}`);
      if (data?.mandate_type !== "search") {
        setErr("not_found");
        return;
      }
      setMandate(data);
    } catch (e) {
      setErr(e?.response?.status === 404 ? "not_found" : "error");
      toast.error(e?.response?.data?.detail || "Errore");
    }
  }, [id]);

  const loadContracts = useCallback(async () => {
    try {
      const { data } = await api.get("/matches/intermediation");
      setContracts(data.filter((c) => c.mandate_id === id || (c.buyer_email && mandate?.signer_email && c.buyer_email === mandate.signer_email)));
    } catch (e) {
      logWarn("SearchDetail: loadContracts failed", e);
    }
  }, [id, mandate?.signer_email]);

  const loadMyProperties = useCallback(async () => {
    if (!user || !["agent", "owner", "admin"].includes(user.role)) return;
    try {
      const { data } = await api.get("/properties/mine");
      setMyProperties(data.filter((p) => p.status === "approved"));
    } catch (e) {
      logWarn("SearchDetail: loadMyProperties failed", e);
    }
  }, [user]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      nav(`/login?next=/searches/${id}`);
      return;
    }
    load();
  }, [loading, user, id, load, nav]);

  useEffect(() => {
    if (mandate && user) {
      loadContracts();
      loadMyProperties();
    }
  }, [mandate, user, loadContracts, loadMyProperties]);

  const downloadContractPdf = async (contract) => {
    try {
      const resp = await api.get(`/matches/intermediation/${contract.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `intermediazione-${contract.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF error");
    }
  };

  if (loading || (!mandate && !err)) {
    return (
      <div className="min-h-screen bg-[#F5F0E6] flex items-center justify-center text-[#7A6E5A]">
        {t("loading", lang)}
      </div>
    );
  }
  if (err === "not_found") {
    return (
      <div className="min-h-screen bg-[#F5F0E6] flex flex-col items-center justify-center p-6">
        <p className="text-4xl font-[Cormorant_Garamond,serif] text-[#2E2A22] mb-3">404</p>
        <p className="text-sm text-[#7A6E5A] mb-6">
          {lang === "it" ? "Ricerca non trovata." : "Search not found."}
        </p>
        <Link to="/dashboard" className="text-sm underline text-[#8a7658]">
          ← Bacheca
        </Link>
      </div>
    );
  }

  const m = mandate || {};
  const signerName = m.signer_name || m.signer_name_masked || "—";
  const isOwner = m.user_id === user?.user_id;
  const isAdmin = user?.role === "admin";
  const canContact = !isOwner && ["agent", "owner", "admin"].includes(user?.role);

  return (
    <div className="min-h-screen bg-[#F5F0E6] text-[#2E2A22]" data-testid="search-detail-page">
      <Header />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1 text-sm text-[#7A6E5A] hover:text-[#2E2A22] transition-colors mb-6"
          data-testid="search-detail-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> {lang === "it" ? "Bacheca" : "Dashboard"}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* LEFT — detail */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880] mb-2">
                {lang === "it" ? "Ricerca / mandato d'acquisto" : "Search / buy mandate"}
              </p>
              {m.property_type && (
                <p className="text-xs tracking-[0.25em] uppercase text-[#B8965A] mb-1" data-testid="search-detail-type">
                  {propertyTypeLabel(m.property_type, lang)}
                </p>
              )}
              <div className="flex items-center gap-3 flex-wrap mb-3">
                <h1
                  className="text-4xl sm:text-5xl font-[Cormorant_Garamond,serif] text-[#1A1610] leading-tight"
                  data-testid="search-detail-signer"
                >
                  {signerName}
                </h1>
                <Badge className="bg-emerald-700 text-white text-[10px] tracking-widest">
                  ACTIVE
                </Badge>
              </div>
              <div data-testid="search-detail-budget" className="text-2xl text-[#C5A880]">
                <Money value={m.search_budget} className="text-2xl text-[#C5A880]" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <InfoTile
                icon={<MapPin />}
                label={t("search_area_label", lang)}
                value={m.search_area || "—"}
                testid="search-detail-area"
              />
              <InfoTile
                icon={<Wallet />}
                label={t("budget_label", lang)}
                value={m.search_budget ? formatMoney(m.search_budget, lang) : "—"}
                testid="search-detail-budget-tile"
              />
              <InfoTile
                icon={<Calendar />}
                label={t("duration_months_label", lang)}
                value={m.exclusivity_months ? `${m.exclusivity_months}` : "—"}
                testid="search-detail-months"
              />
            </div>

            {m.commission_terms && (
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880] mb-2">
                  {t("commission_terms_label", lang)}
                </p>
                <p className="text-[#5A5040] leading-relaxed whitespace-pre-line" data-testid="search-detail-commission">
                  {m.commission_terms}
                </p>
              </div>
            )}

            {m.property_ref && (
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880] mb-2">
                  {lang === "it" ? "Note ricerca" : "Search notes"}
                </p>
                <p className="text-[#5A5040] leading-relaxed whitespace-pre-line" data-testid="search-detail-notes">
                  {m.property_ref}
                </p>
              </div>
            )}

            <div className="flex items-center gap-2 text-xs text-[#8a7658] border-t border-[#D8CFBB] pt-4">
              <SearchIcon className="w-4 h-4" />
              {lang === "it" ? "Riferimento" : "Reference"}:
              <span className="font-mono text-[#2E2A22]">{(m.id || "").slice(0, 8).toUpperCase()}</span>
              <span className="ms-auto text-[#9A8B72]">
                {m.created_at ? new Date(m.created_at).toLocaleDateString() : ""}
              </span>
            </div>

            {/* Existing contracts (only visible to the parties involved) */}
            {contracts.length > 0 && (
              <div className="space-y-3">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">
                  {lang === "it" ? "Trattative in corso" : "Active negotiations"}
                </p>
                <div className="space-y-2" data-testid="search-detail-contracts">
                  {contracts.map((c) => (
                    <ContractRow
                      key={c.id}
                      contract={c}
                      lang={lang}
                      onOpenChat={setChatContract}
                      onDownloadPdf={downloadContractPdf}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT — sticky sidebar */}
          <aside className="space-y-6 lg:sticky lg:top-24 self-start">
            {isOwner && (
              <Card className="bg-[#FBF8F1] border-[#D8CFBB] p-6" data-testid="search-detail-owner-note">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880] mb-2">
                  {lang === "it" ? "La tua ricerca" : "Your search"}
                </p>
                <p className="text-sm text-[#5A5040]">
                  {lang === "it"
                    ? "Questa è la tua ricerca pubblicata. Riceverai una notifica quando un agente ti proporrà un immobile."
                    : "This is your published search. You'll be notified when an agent offers a property."}
                </p>
              </Card>
            )}

            {canContact && (
              <ContactBox
                mandateId={m.id}
                myProperties={myProperties}
                lang={lang}
                onCreated={loadContracts}
              />
            )}

            {isAdmin && !canContact && (
              <Card className="bg-[#FBF8F1] border-[#D8CFBB] p-6" data-testid="search-detail-admin-note">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880] mb-2">Admin</p>
                <p className="text-sm text-[#5A5040]">
                  {lang === "it"
                    ? "Vista amministratore: dati sensibili visibili."
                    : "Admin view: sensitive data visible."}
                </p>
              </Card>
            )}
          </aside>
        </div>
      </main>

      <ChatModal
        contract={chatContract}
        open={!!chatContract}
        onOpenChange={(v) => !v && setChatContract(null)}
      />

      <LegalFooter />
    </div>
  );
}
